// src/data/userService.js
import { db } from "../firebaseConfig";
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";

/* ----------------------------
   Helpers
----------------------------- */
export const normalizeHandle = (h) => (h || "").trim().toLowerCase();

export const isValidHandle = (h) => {
  if (!h) return false;
  if (h.length < 3 || h.length > 20) return false;
  return /^[a-z0-9_]+$/.test(h);
};

/* ----------------------------
   Ensure user doc on login
   - does NOT overwrite createdAt if it already exists
----------------------------- */
export async function ensureUserDoc(user) {
  if (!user?.uid) return;

  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    // First time — create minimal doc
    await setDoc(ref, {
      uid: user.uid,
      createdAt: serverTimestamp(),
      onboardingCompleted: false,
      updatedAt: serverTimestamp(),
    });
  } else {
    // Returning user — just bump lastSeenAt
    await setDoc(ref, { lastSeenAt: serverTimestamp() }, { merge: true });
  }
}

/* ----------------------------
   Public profile read (by uid)
   - used for comments, feed UI, etc.
----------------------------- */
export async function getPublicUser(uid) {
  if (!uid) return null;

  const snap = await getDoc(doc(db, "users", uid));
  if (!snap.exists()) return null;

  const data = snap.data();

  // Normalize location: if stored as a plain string, wrap it
  let location = data.location || null;
  if (typeof location === "string") {
    location = { city: location, visible: true };
  }

  return {
    uid,
    handle: data.handle || null,
    displayName: data.displayName || null,
    photoURL: data.photoURL || null,
    coverPhotoURL: data.coverPhotoURL || null,
    bio: data.bio || null,
    tastePrefs: Array.isArray(data.tastePrefs) ? data.tastePrefs : [],
    reputation: typeof data.reputation === "number" ? data.reputation : 0,
    activity: typeof data.activity === "number" ? data.activity : 0,
    followersCount: typeof data.followersCount === "number" ? data.followersCount : 0,
    followingCount: typeof data.followingCount === "number" ? data.followingCount : 0,
    location,
  };
}

/* ----------------------------
   Public profile read (by handle)
   - used for /u/:handle routes
----------------------------- */
export async function getPublicUserByHandle(rawHandle) {
  const handle = normalizeHandle(rawHandle);
  if (!isValidHandle(handle)) return null;

  const handleSnap = await getDoc(doc(db, "handles", handle));
  if (!handleSnap.exists()) return null;

  const { uid } = handleSnap.data() || {};
  if (!uid) return null;

  return await getPublicUser(uid);
}

/* ----------------------------
   Reserve / change unique handle
   handles/{handle} = { uid, handle, createdAt }
   users/{uid}.handle = handle

   - atomic
   - releases old handle if changed
----------------------------- */
export async function setHandle(uid, rawHandle) {
  const handle = normalizeHandle(rawHandle);

  if (!uid) throw new Error("Missing uid");
  if (!isValidHandle(handle)) throw new Error("Invalid handle");

  const userRef = doc(db, "users", uid);
  const handleRef = doc(db, "handles", handle);

  await runTransaction(db, async (tx) => {
    const userSnap = await tx.get(userRef);
    const handleSnap = await tx.get(handleRef);

    const currentHandle = userSnap.exists() ? userSnap.data().handle : null;

    // Already taken by someone else
    if (handleSnap.exists() && handleSnap.data().uid !== uid) {
      throw new Error("Username already taken");
    }

    // Reserve handle (or no-op if it already belongs to uid)
    if (!handleSnap.exists()) {
      tx.set(handleRef, { uid, handle, createdAt: serverTimestamp() });
    }

    // Update user doc with new handle
    tx.set(
      userRef,
      { uid, handle, updatedAt: serverTimestamp() },
      { merge: true }
    );

    // Release old handle if changed
    if (currentHandle && normalizeHandle(currentHandle) !== handle) {
      tx.delete(doc(db, "handles", normalizeHandle(currentHandle)));
    }
  });

  return handle;
}

/* ----------------------------
   Optional: clear handle (release it)
   - useful if you ever add "delete account" or "change handle later" UX
----------------------------- */
export async function clearHandle(uid) {
  if (!uid) throw new Error("Missing uid");

  const userRef = doc(db, "users", uid);

  await runTransaction(db, async (tx) => {
    const userSnap = await tx.get(userRef);
    if (!userSnap.exists()) return;

    const currentHandle = userSnap.data().handle;
    if (currentHandle) {
      tx.delete(doc(db, "handles", normalizeHandle(currentHandle)));
    }

    tx.set(userRef, { handle: null, updatedAt: serverTimestamp() }, { merge: true });
  });
}

/* ----------------------------
   Update profile fields
----------------------------- */
export async function updateProfile(uid, { displayName, bio, photoURL, coverPhotoURL, location, homeCity, defaultRadiusMi, phoneNumber } = {}) {
  if (!uid) throw new Error("Missing uid");

  const ref = doc(db, "users", uid);

  const patch = { updatedAt: serverTimestamp() };

  if (displayName !== undefined) {
    patch.displayName = String(displayName).trim().slice(0, 40);
  }

  if (bio !== undefined) {
    patch.bio = String(bio).trim().slice(0, 160);
  }

  if (photoURL !== undefined) {
    patch.photoURL = photoURL || null;
  }

  if (coverPhotoURL !== undefined) {
    patch.coverPhotoURL = coverPhotoURL || null;
  }

  if (location !== undefined) {
    if (!location) {
      patch.location = null;
    } else if (typeof location === "string") {
      patch.location = { city: location.trim().slice(0, 100), visible: true };
    } else {
      patch.location = {
        city: String(location.city || "").trim().slice(0, 100),
        visible: location.visible !== false,
      };
    }
  }

  if (homeCity !== undefined) {
    patch.homeCity = homeCity ? String(homeCity).trim().slice(0, 100) : null;
  }

  if (defaultRadiusMi !== undefined) {
    patch.defaultRadiusMi = typeof defaultRadiusMi === "number" ? defaultRadiusMi : null;
  }

  // phoneNumber is contact PII — it goes to the owner-only
  // users/{uid}/private/contact doc, never the world-readable profile doc
  // (C4). The shared Firestore rules no longer allow phoneNumber on the
  // users doc, so it's written separately below.

  await updateDoc(ref, patch);

  if (phoneNumber !== undefined) {
    const trimmed = phoneNumber ? String(phoneNumber).trim() : "";
    await setDoc(
      doc(db, "users", uid, "private", "contact"),
      { phoneNumber: trimmed || null, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
}

/* ----------------------------
   Owner-only contact info
   Reads users/{uid}/private/contact — the phoneNumber relocated off the
   public profile (C4). Rules allow only the owner to read it. Returns {}
   when absent or on error.
----------------------------- */
export async function getPrivateContact(uid) {
  if (!uid) return {};
  try {
    const snap = await getDoc(doc(db, "users", uid, "private", "contact"));
    return snap.exists() ? snap.data() || {} : {};
  } catch {
    return {};
  }
}

/* ----------------------------
   Reputation / Activity (for your profile mock)
   - call these from places where you want to award points
----------------------------- */
export async function bumpReputation(uid, delta = 0) {
  if (!uid) throw new Error("Missing uid");
  if (!Number.isFinite(delta) || delta === 0) return;

  const userRef = doc(db, "users", uid);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(userRef);
    const cur = snap.exists() ? snap.data() : {};
    const current = typeof cur.reputation === "number" ? cur.reputation : 0;

    tx.set(
      userRef,
      {
        reputation: Math.max(0, current + delta),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
}

export async function bumpActivity(uid, delta = 0) {
  if (!uid) throw new Error("Missing uid");
  if (!Number.isFinite(delta) || delta === 0) return;

  const userRef = doc(db, "users", uid);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(userRef);
    const cur = snap.exists() ? snap.data() : {};
    const current = typeof cur.activity === "number" ? cur.activity : 0;

    tx.set(
      userRef,
      {
        activity: Math.max(0, current + delta),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
}
